<template>
  <div class="view-lesson">
    <action-dispatcher
      action="courses/getLesson"
      loading-class="my-5"
      :parameters="{
        lesson_id: $route.params.id
      }"
    >
      <header class="page-header">
        <div class="container py-5 text-light">
          <div
            class="d-flex justify-content-center align-items-center mb-5 page-title"
          >
            <span class="badge">{{ lesson.number }}</span>
            <div class="d-flex flex-column text-left">
              <span class="h1 mb-0"> GW {{ date }} </span>
              <span class="text-secondary">{{ lesson.name }}</span>
            </div>
          </div>

          <div class="mb-5">
            <video-player
              v-if="lesson.featured"
              :lesson="lesson.featured"
              :progress="lesson.progress"
            />
          </div>

          <div v-if="caColor || caBlack" class="row pt-4">
            <div class="col-lg-5">
              <img
                :src="lesson.ca_cover || lesson.app_cover"
                class="img-fluid d-block mx-auto mb-5 mb-lg-0"
              />
            </div>
            <div class="col-lg-6 offset-lg-1">
              <h4 class="mb-4">
                <i class="fas fa-book-open mr-2"></i> {{ category }}
              </h4>
              <div v-html="lessonDescription" />
              <div class="d-flex flex-column align-items-start">
                <button
                  v-if="!isGettingStarted"
                  @click="read(caColor)"
                  class="btn btn-primary px-5 py-2 strong mb-3"
                >
                  LER CA
                </button>
                <button
                  v-if="caBlack"
                  @click="read(caBlack)"
                  class="btn text-light"
                >
                  <u>Ler versão preto e branco</u>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section
        v-if="shares.length === 1 || hasTranscription"
        class="bg-light-gray py-5"
      >
        <div class="container">
          <div class="row">
            <div v-if="shares.length === 1" class="col-lg-4 pb-5 pb-lg-0">
              <share :file="shares[0].file" />
            </div>

            <template v-if="hasTranscription">
              <div class="col-lg-4 offset-lg-1 col-6">
                <h5 class="mb-4">
                  <i class="far fa-file-alt mr-2"></i> Transcrições
                </h5>
                <img
                  class="img-fluid"
                  src="@/assets/images/guerrilha-way/transcricao.jpg"
                />
              </div>
              <div class="col-lg-3 pt-5 col-6">
                <transcriptions
                  :date="date"
                  :black="transcriptionBlack"
                  :color="transcriptionColor"
                  @read="read"
                />
              </div>
            </template>
          </div>
        </div>
      </section>

      <section v-if="videos.length" class="bg-light py-5">
        <div class="container">
          <h5><i class="fas fa-video mr-2 mb-3"></i> Vídeos</h5>
          <videos :videos="videos" />
        </div>
      </section>

      <section v-if="audios.length" class="bg-light-gray py-5">
        <div class="container">
          <h5><i class="fas fa-podcast mr-2 mb-3"></i> Audios da semana</h5>
          <audios :audios="audios" />
        </div>
      </section>

      <section v-if="shares.length > 1" class="bg-light py-5">
        <div class="container">
          <div class="row">
            <div
              v-for="(share, index) in shares"
              :key="index"
              class="col-lg-4 pb-5 pb-lg-0"
            >
              <share :file="share.file" />
            </div>
          </div>
        </div>
      </section>

      <section v-if="pdfs.length" class="bg-light py-5">
        <div class="container">
          <h5><i class="fas fa-file-pdf mr-2 mb-3"></i> PDFs</h5>
          <pdfs :pdfs="pdfs" />
        </div>
      </section>

      <transition name="fade">
        <pdf-reader
          v-if="reading"
          :resource="selectedFile"
          :lesson="lesson"
          @close="reading = false"
        />
      </transition>
    </action-dispatcher>
  </div>
</template>

<script>
import { mapState } from "vuex";
import get from "lodash/get";
import isMobile from "ismobilejs";
import PdfReader from "@/components/lesson/PdfReader";
import VideoPlayer from "@/components/lesson/VideoPlayer";
import Transcriptions from "@/components/lesson/Transcriptions";
import Videos from "@/components/lesson/Videos";
import Audios from "@/components/lesson/Audios";
import Pdfs from "@/components/lesson/Pdfs";
import Share from "@/components/lesson/Share";

const months = [
  "",
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro"
];

const oldWeekdaySchedule = "segunda a sexta";
const oldSaturdaySchedule = "sabado";

const normalizeText = value =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const findScheduleLine = (container, schedule) => {
  const elements = Array.from(container.querySelectorAll("p, li, div"));

  return elements.find(element => {
    const hasSchedule = normalizeText(element.textContent).includes(schedule);
    const childHasSchedule = Array.from(element.children).some(child =>
      normalizeText(child.textContent).includes(schedule)
    );

    return hasSchedule && !childHasSchedule;
  });
};

const updateTrainingSchedule = description => {
  const container = document.createElement("div");
  container.innerHTML = description;

  const weekdayLine = findScheduleLine(container, oldWeekdaySchedule);

  if (!weekdayLine) {
    return description;
  }

  const mondayToThursday = document.createElement("p");
  mondayToThursday.classList.add("mb-0");
  mondayToThursday.textContent = "🕘 Segunda a quinta: 7h, 10h e 20h30";

  const friday = document.createElement("p");
  friday.textContent = "🕘 Sexta-feira: 7h, 10h e 18h30";

  weekdayLine.before(mondayToThursday, friday);
  weekdayLine.remove();

  const saturdayLine = findScheduleLine(container, oldSaturdaySchedule);
  saturdayLine?.remove();

  return container.innerHTML;
};

export default {
  components: {
    PdfReader,
    VideoPlayer,
    Transcriptions,
    Videos,
    Audios,
    Pdfs,
    Share
  },
  data() {
    return {
      reading: false,
      selectedFile: null
    };
  },
  computed: {
    ...mapState("courses", ["lesson"]),
    resources() {
      return get(this.lesson, "resources", []);
    },
    shares() {
      return this.resources.filter(r => r.category_key === "share");
    },
    date() {
      return `${months[this.lesson.month]} – ${this.lesson.year}`;
    },
    caBlack() {
      return this.resources.find(
        r => r.category_key === "ca" && r.is_blackwhite
      );
    },
    caColor() {
      return this.resources.find(
        r => r.category_key === "ca" && !r.is_blackwhite
      );
    },
    category() {
      const category = this.caColor?.category || this.caBlack?.category;

      if (!category || /carta/i.test(category)) {
        return "Caderno de ativação";
      }

      return category;
    },
    isGettingStarted() {
      return this.lesson.category_cache?.slug === "comece-por-aqui";
    },
    lessonDescription() {
      const description = this.lesson.description || "";

      if (!this.isGettingStarted) {
        return description;
      }

      return updateTrainingSchedule(description);
    },
    transcriptionBlack() {
      return this.resources.find(
        r => r.category_key === "transcription" && r.is_blackwhite
      );
    },
    transcriptionColor() {
      return this.resources.find(
        r => r.category_key === "transcription" && !r.is_blackwhite
      );
    },
    hasTranscription() {
      return this.transcriptionBlack || this.transcriptionColor;
    },
    videos() {
      return this.resources.filter(r =>
        ["videos", "lives"].includes(r.category_key)
      );
    },
    audios() {
      return this.resources.filter(r => r.category_key === "audios");
    },
    pdfs() {
      return this.resources.filter(r => r.category_key === "pdf");
    }
  },
  methods: {
    read(resource) {
      if (resource.external_link) {
        window.open(resource.external_link, "_blank");
      } else if (isMobile(window.navigator).any) {
        window.open(resource.file, "_blank");
      } else {
        this.selectedFile = resource;
        this.reading = true;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-lesson {
  .page-title {
    .badge {
      font-size: 32px;
      min-width: 72px;
      min-height: 72px;
      max-width: 72px;
      max-height: 72px;
      border-radius: 72px;
      border: 3px solid #fdfdfd;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
    }
  }
}
</style>
